import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Radio } from 'lucide-react'

import { SectionLabel } from '@/components/px/SectionLabel'
import { epochLabel } from '@/features/projects/format'
import { useWebSocketStatus } from '@/providers/webSocketContext'
import { useActivityContext } from '@/services/websocket/useActivityContext'
import { useAppDispatch, useAppSelector } from '@/store'
import {
  fetchActivityFeedFirstPage,
  fetchActivityFeedNextPage,
  feedProjectSet,
  feedFiltersSet,
  feedClosed,
  selectActivityFeedAuthors,
  selectActivityFeedEntries,
  selectActivityFeedError,
  selectActivityFeedFilters,
  selectActivityFeedHasMore,
  selectActivityFeedLoading,
  selectActivityFeedLoadingMore,
  selectActivityFeedNonce
} from '../slices/activityFeedSlice'

/**
 * The project activity feed panel (AC-5): rows from the cursor-paginated
 * endpoint, live inserts via the bus — a frame for THIS project bumps the
 * nonce, the effect refetches PAGE 1 with no manual refresh. Another
 * project's activity never disturbs the feed (the slice guards the
 * projectId). Filters are the loaded rows' own distinct types + authors (a
 * closed list — no invented type registry).
 *
 * TASK-7922504E: the panel holds `project:<projectId>` on the org's activity
 * socket for as long as it is mounted — the context a client-side session
 * (which holds no `org:` firehose) receives this project's activity on. The
 * badge reads Live only when the socket is up AND that context is granted.
 */
export function ProjectActivityFeed({ orgId, projectId }: { orgId: string; projectId: string }) {
  const { t } = useTranslation('projectWorkspace')
  const dispatch = useAppDispatch()
  const { isConnected } = useWebSocketStatus()
  const contextId = `project:${projectId}`
  const contextIds = useMemo(() => [contextId], [contextId])
  const { [contextId]: liveContext } = useActivityContext(contextIds)
  const liveState = liveContext?.state ?? 'pending'
  const live = isConnected && liveState === 'granted'
  const badgeState = live ? 'live' : liveState === 'refused' ? 'unavailable' : 'reconnecting'
  const liveLabel =
    badgeState === 'live'
      ? t('activity.live')
      : badgeState === 'unavailable'
        ? t('activity.liveUnavailable')
        : t('activity.reconnecting')

  const entries = useAppSelector(selectActivityFeedEntries)
  const loading = useAppSelector(selectActivityFeedLoading)
  const loadingMore = useAppSelector(selectActivityFeedLoadingMore)
  const error = useAppSelector(selectActivityFeedError)
  const hasMore = useAppSelector(selectActivityFeedHasMore)
  const nonce = useAppSelector(selectActivityFeedNonce)
  const filters = useAppSelector(selectActivityFeedFilters)
  const authors = useAppSelector(selectActivityFeedAuthors)

  // Mount: claim the project, then page 1. Unmount: release.
  useEffect(() => {
    dispatch(feedProjectSet(projectId))
    return () => {
      dispatch(feedClosed())
    }
  }, [dispatch, projectId])

  // First page on mount, on every filter change, and on every nonce bump
  // (a live insert refetched here — never delta-merged into `entries`).
  useEffect(() => {
    void dispatch(fetchActivityFeedFirstPage({ orgId, projectId }))
  }, [dispatch, orgId, projectId, filters, nonce])

  // Distinct types of the LOADED rows (the closed filter list).
  const typeOptions = useMemo(
    () => [...new Set(entries.map((entry) => entry.type).filter((type): type is string => typeof type === 'string'))],
    [entries]
  )

  const onTypeChanged = (value: string): void => {
    dispatch(feedFiltersSet({ ...filters, type: value === '' ? null : value }))
  }

  const onActorChanged = (value: string): void => {
    dispatch(feedFiltersSet({ ...filters, actorId: value === '' ? null : value }))
  }

  return (
    <section aria-label={t('activity.panelTitle')} className="rounded-lg border border-border bg-card px-4 py-3.5">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div className="flex items-center gap-2">
          <SectionLabel>{t('activity.panelTitle')}</SectionLabel>
          <span
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[0.68rem] font-medium ${
              live ? 'bg-done-bg text-done' : 'bg-muted text-muted-foreground'
            }`}
            data-testid="activity-live-badge"
            data-live-state={badgeState}
          >
            <Radio className="h-3 w-3" aria-hidden />
            {liveLabel}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <label className="flex items-center gap-1">
            <span className="text-muted-foreground">{t('activity.filterType')}</span>
            <select
              value={filters.type ?? ''}
              onChange={(event) => onTypeChanged(event.target.value)}
              aria-label={t('activity.filterType')}
              className="rounded-md border border-input bg-card px-1.5 py-1 text-xs text-foreground"
            >
              <option value="">{t('activity.allTypes')}</option>
              {typeOptions.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-1">
            <span className="text-muted-foreground">{t('activity.filterActor')}</span>
            <select
              value={filters.actorId ?? ''}
              onChange={(event) => onActorChanged(event.target.value)}
              aria-label={t('activity.filterActor')}
              className="rounded-md border border-input bg-card px-1.5 py-1 text-xs text-foreground"
            >
              <option value="">{t('activity.allActors')}</option>
              {authors.map((author) => (
                <option key={author.id} value={author.id}>
                  {author.name}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {error !== null && (
        <div role="alert" className="mt-2 rounded-md border border-blocked bg-blocked-bg px-3 py-2 text-sm text-foreground">
          <p>{t('activity.error')}</p>
          <button
            type="button"
            onClick={() => {
              // Branched (not a ternary union): two distinct AsyncThunkActions
              // cannot share one overload resolution.
              if (error.source === 'nextPage') {
                void dispatch(fetchActivityFeedNextPage({ orgId, projectId }))
              } else {
                void dispatch(fetchActivityFeedFirstPage({ orgId, projectId }))
              }
            }}
            className="mt-1 text-xs font-medium text-accent-foreground hover:underline"
          >
            {t('activity.retry')}
          </button>
        </div>
      )}

      {loading ? (
        <p role="status" className="mt-1.5 text-sm text-muted-foreground">
          {t('activity.loadingMore')}
        </p>
      ) : entries.length === 0 ? (
        <p className="mt-1.5 text-sm text-muted-foreground" data-testid="activity-empty">
          {filters.type !== null || filters.actorId !== null ? t('activity.emptyFiltered') : t('activity.empty')}
        </p>
      ) : (
        <ul className="mt-1.5" data-testid="activity-entries">
          {entries.map((entry) => (
            <li key={entry.id} className="v-row py-2">
              <div className="flex flex-wrap items-baseline gap-x-2">
                <span className="font-mono text-[0.68rem] font-semibold tracking-[0.04em] text-muted-foreground">
                  {entry.type ?? '—'}
                </span>
                <span className="text-xs text-muted-foreground">{epochLabel(entry.createdAt)}</span>
                {entry.author?.name !== undefined && (
                  <span className="text-xs font-medium text-ink-soft">{entry.author.name}</span>
                )}
              </div>
              {entry.content !== undefined && entry.content !== '' && (
                <p className="mt-0.5 text-sm text-ink-soft">{entry.content}</p>
              )}
            </li>
          ))}
        </ul>
      )}

      {hasMore && !loading && (
        <button
          type="button"
          disabled={loadingMore}
          onClick={() => void dispatch(fetchActivityFeedNextPage({ orgId, projectId }))}
          className="mt-2 rounded-md border border-input bg-card px-2.5 py-1 text-xs font-medium text-foreground hover:bg-muted disabled:opacity-50"
          data-testid="activity-load-more"
        >
          {loadingMore ? t('activity.loadingMore') : t('activity.loadMore')}
        </button>
      )}
    </section>
  )
}
